import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useToast } from '../components/Toast';

export interface KbArticleSummary {
  id: string; title: string; category?: string | null; tags: string[];
  visibility: string; isPublished: boolean; views: number; updatedAt: string;
  resourceType: 'article' | 'link'; externalUrl?: string | null;
}
export interface KbArticle extends KbArticleSummary { body: string | null; }

export function useKnowledgeBase() {
  const { toast } = useToast();
  const [articles, setArticles] = useState<KbArticleSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/kb', { params: { search: search || undefined } });
      setArticles(res.data.articles ?? []);
    } catch (e: any) {
      toast(e?.response?.data?.message || 'Failed to load articles', 'error');
    } finally {
      setLoading(false);
    }
  }, [search, toast]);

  useEffect(() => { refetch(); }, [refetch]);

  const get = async (id: string): Promise<KbArticle | null> => {
    try { return (await api.get(`/kb/${id}`)).data.article; }
    catch { return null; }
  };
  const create = async (data: Partial<KbArticle>): Promise<string | null> => {
    try {
      const res = await api.post('/kb', data);
      toast('Article created', 'success'); await refetch();
      return res.data.article.id as string;
    } catch (e: any) { toast(e?.response?.data?.message || 'Create failed', 'error'); return null; }
  };
  const update = async (id: string, data: Partial<KbArticle>): Promise<string | null> => {
    try { await api.put(`/kb/${id}`, data); toast('Article updated', 'success'); await refetch(); return id; }
    catch (e: any) { toast(e?.response?.data?.message || 'Update failed', 'error'); return null; }
  };
  const remove = async (id: string) => {
    try { await api.delete(`/kb/${id}`); toast('Article removed', 'success'); await refetch(); }
    catch (e: any) { toast(e?.response?.data?.message || 'Delete failed', 'error'); }
  };

  return { articles, loading, search, setSearch, refetch, get, create, update, remove };
}
